import { NARRATOR_LABELS, normalizeNarrator, SettingKey } from "@serophin/nottldr-shared";
import { BookOpen, Headphones, Mail, Monitor, User } from "lucide-preact";
import type { SettingsMenuItemProps } from "./SettingsMenuItem";
import { SettingsMenuItem } from "./SettingsMenuItem";
import type { SettingsState } from "./useSettingsState";

export type SettingsTab = "narrator" | "appearance" | "reading" | "email" | "account";

export type SettingsMenuProps = {
  settings: SettingsState;
  email?: string;
  onTabChange: (tab: SettingsTab) => void;
};

type Row = Omit<SettingsMenuItemProps, "onClick"> & { tab: SettingsTab };

const DIVIDER = "border-b border-gray-200 dark:border-gray-700";

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** The rows of the menu; the email and account rows are for signed-in visitors only (see `hidden-anonymous` in site.css). */
const rowsOf = (settings: SettingsState, email: string | undefined): Row[] => [
  {
    tab: "narrator",
    icon: Headphones,
    title: "Audio Narrator",
    currentValue: NARRATOR_LABELS[normalizeNarrator(settings.get(SettingKey.NARRATOR))],
    class: DIVIDER,
    testId: "settings-audio-narrator",
  },
  {
    tab: "reading",
    icon: BookOpen,
    title: "Reading",
    currentValue: settings.get(SettingKey.SUMMARY_EXPANDED) === "on" ? "Summaries expanded" : "Summaries collapsed",
    class: DIVIDER,
    testId: "settings-reading",
  },
  {
    tab: "appearance",
    icon: Monitor,
    title: "Appearance",
    currentValue: capitalize(settings.get(SettingKey.THEME)),
    class: DIVIDER,
    testId: "settings-appearance",
  },
  {
    tab: "email",
    icon: Mail,
    title: "Email Preferences",
    description: "Manage your email notifications",
    class: `hidden-anonymous ${DIVIDER}`,
    testId: "settings-email",
  },
  { tab: "account", icon: User, title: "Account Settings", currentValue: email, class: "hidden-anonymous", testId: "settings-account" },
];

export const SettingsMenu = ({ settings, email, onTabChange }: SettingsMenuProps) => (
  <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
    {rowsOf(settings, email).map(({ tab, ...row }) => (
      <SettingsMenuItem key={tab} {...row} onClick={() => onTabChange(tab)} />
    ))}
  </div>
);
